import React from 'react';
import { useGateways } from '../context/GatewayContext';
import { Button } from './ui/Button';
import { useWalletContext } from '../context/WalletContext';

const GatewayIcon = () => (
  <svg width="24" height="24" viewBox="0 0 64 65" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#00FF84]">
    <path d="M47.9994 11.6177H15.9993C13.0538 11.6177 10.666 14.0055 10.666 16.951V48.951C10.666 51.8965 13.0538 54.2843 15.9993 54.2843H47.9994C50.9449 54.2843 53.3327 51.8965 53.3327 48.951V16.951C53.3327 14.0055 50.9449 11.6177 47.9994 11.6177Z" stroke="currentColor" strokeWidth="5.33333" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M40 6.28418V11.6175" stroke="currentColor" strokeWidth="5.33333" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M40 54.2842V59.6175" stroke="currentColor" strokeWidth="5.33333" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5.33301 40.9509H10.6663" stroke="currentColor" strokeWidth="5.33333" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5.33301 24.9509H10.6663" stroke="currentColor" strokeWidth="5.33333" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M53.334 40.9509H58.6673" stroke="currentColor" strokeWidth="5.33333" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M53.334 24.9509H58.6673" stroke="currentColor" strokeWidth="5.33333" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M24 6.28418V11.6175" stroke="currentColor" strokeWidth="5.33333" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M24 54.2842V59.6175" stroke="currentColor" strokeWidth="5.33333" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M22.5448 32.4524L33.3187 19.3756C34.0884 18.4414 35.594 19.1699 35.3392 20.3531L33.2294 30.1491C33.0672 30.9025 33.6742 31.6004 34.4428 31.5442L39.8921 31.1454C40.9285 31.0696 41.53 32.2943 40.8364 33.0682L28.7424 46.5613C27.9183 47.4807 26.4203 46.6395 26.7765 45.4573L29.9604 34.8881C30.2092 34.0622 29.4979 33.265 28.6492 33.4184L23.6416 34.3234C22.5832 34.5146 21.8609 33.2825 22.5448 32.4524Z" fill="currentColor"/>
  </svg>
);

export const GatewaySigningStep = () => {
  const { gateways, fetchGatewayStates, isLoading, suppressSigningStep } = useGateways();
  const { disconnect, publicKey } = useWalletContext();

  if (suppressSigningStep || gateways.length === 0) {
    return null;
  }

  // Calculate if scrolling should be applied
  const shouldScroll = gateways.length > 3;

  return (
    <div className="fixed inset-0 bg-[#131313] flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-lg bg-[#1D1D1D] rounded-lg border border-[#292929] shadow-xl">
        <div className="p-6 space-y-6">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-white">
              Gateway Access Required
            </h1>
            <p className="text-[#999999]">
              Sign to access your {gateways.length} gateway{gateways.length > 1 ? 's' : ''}
            </p>
          </div>

          <div className={`space-y-3 rounded-xl bg-[#131313] border border-[#292929] p-4 ${shouldScroll ? 'max-h-[250px] overflow-y-auto' : ''}`}>
            {gateways.map((gateway) => (
              <div 
                key={gateway.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-[#1D1D1D] border border-[#292929]"
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#00FF84]/10 text-[#00FF84]">
                  <GatewayIcon />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-white">
                    {gateway.name}
                  </div>
                  <div className="truncate text-[#666666] text-sm font-mono">
                    {gateway.id}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-4">
            <Button
              onClick={() => fetchGatewayStates()}
              disabled={isLoading}
              className="w-full py-4 text-base font-semibold rounded-xl transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle 
                      className="opacity-25" 
                      cx="12" 
                      cy="12" 
                      r="10" 
                      stroke="currentColor" 
                      strokeWidth="4"
                      fill="none"
                    />
                    <path 
                      className="opacity-75" 
                      fill="currentColor" 
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Signing...</span>
                </div>
              ) : (
                'Sign to Continue'
              )}
            </Button>

            <div className="space-y-3">
              <p className="text-center text-sm text-[#999999]">
                This signature will grant you access to manage your gateways
              </p>

              <button
                onClick={disconnect}
                className="w-full text-sm text-[#666666] hover:text-[#999999] transition-colors space-y-1"
              >
                <div>Disconnect Wallet</div>
                <div className="truncate font-mono text-xs">
                  {publicKey?.toString()}
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 